import * as React from 'react';
import {colorSlate, colorWhite} from '../theme/color';
import {classes, style} from 'typestyle';
import {
  betweenJustified,
  horizontal,
  horizontallySpaced,
  margin,
  maxWidth,
  vertical,
  verticallySpaced,
  width,
  wrap
} from 'csstips';
import {AppFooterSection, FooterSection} from './app-footer-section';
import {AppFooterCopyright} from './app-footer-copyright';
import {mediaTablet, mediaMobileOnly, mediaDesktop} from '../theme/media';
import {constantPlayerHeight} from '../theme/constant';
import {transitionQuickEase} from '../theme/transition';
import {observer} from 'mobx-react';
import {em, percent} from 'csx';
import {ApiContentData} from '../modules/content/model';
import {Component, deps, inject} from '../lib/component';
import {Logo} from './logo';


export interface AppFooterProps {
  apiContent: ApiContentData;
  youTubeImage: string;
  abbeyRoadImage: string;
  logoImage: string;
  midemImage: string;
  exampleLicense?: string;
  expanded?: boolean;
  Follow: any;

  onClickLink(event: React.MouseEvent<any>): void;

  i18n?: {
    footerBlurb: string;
    customerCare: FooterSection;
    ourPolicies: FooterSection;
    ourMusic: FooterSection;
    musicGenres: FooterSection;
    musicThemes: FooterSection;
    ourCompany: FooterSection;
    ourLabels: FooterSection;
    ourLicences: FooterSection;
    platforms: FooterSection;
    learn: FooterSection;
    rightsholders: FooterSection;
    myAccount: FooterSection;
  }
}

@inject(deps) @observer
export class AppFooter extends Component<AppFooterProps> {

  render() {
    const {youTubeImage, abbeyRoadImage, midemImage, logoImage, Follow, expanded, onClickLink, exampleLicense} = this.props;

    const i18n = this.props.i18n
      ? {...AppFooter.i18n, ...this.props.i18n}
      : AppFooter.i18n;

    const licences: FooterSection = {...i18n.ourLicences, items: [...i18n.ourLicences.items]};

    if (exampleLicense) {
      licences.items.push({text: 'Example license', path: exampleLicense, external: true})
    }

    return (
      <div className={classes(AppFooter.styles.container, expanded && AppFooter.styles.containerExpanded)}>
        <div className={AppFooter.styles.innerContainer}>
          <AppFooterSection onClickLink={onClickLink} {...i18n.ourCompany} />
          <AppFooterSection onClickLink={onClickLink} {...licences} />
          <AppFooterSection onClickLink={onClickLink} {...i18n.platforms} />
          <AppFooterSection onClickLink={onClickLink} {...i18n.learn} />
          <AppFooterSection onClickLink={onClickLink} {...i18n.customerCare} />
          <AppFooterSection onClickLink={onClickLink} {...i18n.rightsholders} />
          <AppFooterSection onClickLink={onClickLink} {...i18n.ourPolicies} />
        </div>
        <div className={AppFooter.styles.blurb}>
          <div className={AppFooter.styles.logo}>
            <Logo />
          </div>
          <div>
            {i18n.footerBlurb}
          </div>
          <div>
            <Follow />
          </div>
        </div>
        <AppFooterCopyright
          logoImage={logoImage}
          youTubeImage={youTubeImage}
          abbeyRoadImage={abbeyRoadImage}
          midemImage={midemImage}
          onClickLink={onClickLink}
          Follow={Follow}
        />
      </div>
    )
  }

  static i18n = {
    footerBlurb: 'Lickd is a digital platform helping YouTube content creators legally use the music they love. We provide commercial music from real labels for licensing in YouTube videos without the fear of a Creator losing their ad revenue to a Copyright Claim.',
    customerCare: {
      title: 'Customer care',
      items: [
        {text: 'Contact', path: '/contact'},
        {text: 'FAQ', path: '/faq'},
      ]
    },
    ourPolicies: {
      title: 'Our policies',
      items: [
        {text: 'Terms & conditions', path: '/terms-and-conditions'},
        {text: 'Cookie policy', path: '/cookie-policy'},
        {text: 'Privacy policy', path: '/privacy-policy'},
      ]
    },
    ourCompany: {
      title: 'Our company',
      items: [
        {text: 'About', path: '/about'},
        {text: 'Licensing', path: '/licensing'},
        {text: 'Music submissions', path: '/music-submissions'},
        {text: 'Careers', path: '/careers'},
        {text: 'Blog', path: 'https://lickd.co/blog'},
      ]
    },
    ourLicences: {
      title: 'Our licences',
      items: [
        {text: 'Pricing', path: '/pricing'},
        {text: 'Standard', path: '/learn/standard-licence'},
        {text: 'Brand sponsored', path: '/learn/brand-sponsored-licence'},
        {text: 'Branded content', path: '/learn/branded-content-licence'},
      ]
    },
    platforms: {
      title: 'Video platforms',
      items: [
        {text: 'YouTube & Content ID', path: '/learn/youtube-and-contentid'},
        {text: 'Facebook & Instagram', path: '/learn/facebook-and-instagram'},
        {text: 'Twitch', path: '/learn/twitch'},
        {text: 'Vimeo', path: '/learn/vimeo'},
        {text: 'Dailymotion', path: '/learn/dailymotion'},
      ]
    },
    learn: {
      title: 'Customers',
      items: [
        {text: 'For Creators', path: '/learn/for-creators'},
        {text: 'For MCNs', path: '/learn/for-multi-channel-networks'},
      ]
    },
    rightsholders: {
      title: 'Rightsholders',
      items: [
        {text: 'For artists', path: '/learn/for-artists'},
        {text: 'For distributors', path: '/learn/for-distributors'},
        {text: 'For record labels', path: '/learn/for-record-labels'},
      ]
    },
  };

  static styles = {
    container: style(
      {
        ...vertical,
        ...verticallySpaced(60),
        background: colorSlate.darken(0.05).toString(),
        color: colorWhite.toString(),
        transition: `padding ${transitionQuickEase}`
      },
      mediaMobileOnly({
        padding: '60px 20px 20px',
        width: percent(100),
        $nest: {
          '&> div:first-child': {
            ...verticallySpaced(60)
          }
        }
      }),
      mediaTablet({
        padding: '60px 40px 40px',
        $nest: {
          '&> div:first-child': {
            ...horizontal,
            ...wrap,
            ...horizontallySpaced(40),
            marginLeft: percent(7.5),
            marginBottom: '30px',
          }
        }
      })
    ),
    containerExpanded: style({
      paddingBottom: `calc(40px + ${constantPlayerHeight}) !important`
    }),
    innerContainer: style(
      mediaMobileOnly({
        flexFlow: 'row wrap',
        justifyContent: 'space-between',
        display:'flex'
      })
    ),
    blurb: style(
      {
        justifyContent: 'center',
        ...vertical,
        ...wrap,
        ...betweenJustified,
        $nest: {
          '&> div': {
            marginBottom: em(2),
          }
        },
      },
      mediaDesktop({
        justifyContent: 'center',
        ...horizontal,
        ...horizontallySpaced(em(5)),
        flexWrap: 'nowrap',
        $nest: {
          '&> div': {
            maxWidth: (percent(50)),
          }
        },
      })
    ),
    logo: style({
      margin: '2em auto',
    },
      mediaDesktop({
        margin: 'inherit'
      })
    )
  }
}